import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Package, Plus, Pencil, Trash2, Loader2, Calendar, Bug, Shield,
  ArrowLeft, Target, Play, Clock, CheckCircle, AlertTriangle, ChevronRight, CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productTypes = [
  { value: 'web_application', label: 'Web Application' },
  { value: 'api_service', label: 'API Service' },
  { value: 'mobile_app', label: 'Mobile Application' },
  { value: 'desktop_app', label: 'Desktop Application' },
  { value: 'cloud_infrastructure', label: 'Cloud Infrastructure' },
  { value: 'network_device', label: 'Network Device' },
  { value: 'iot_device', label: 'IoT Device' },
  { value: 'container_service', label: 'Container/Microservice' },
  { value: 'firmware', label: 'Firmware' },
];

const lifecycles = [
  { value: 'development', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
  { value: 'eol', label: 'End of Life' },
];

const criticalities = [
  { value: 'critical', label: 'Critical', color: 'border-red-500/30 text-red-500 bg-red-500/10' },
  { value: 'high', label: 'High', color: 'border-orange-500/30 text-orange-500 bg-orange-500/10' },
  { value: 'medium', label: 'Medium', color: 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10' },
  { value: 'low', label: 'Low', color: 'border-green-500/30 text-green-500 bg-green-500/10' },
];

const engagementTypes = [
  { value: 'interactive', label: 'Interactive Testing' },
  { value: 'cicd', label: 'CI/CD Scan' },
  { value: 'api_test', label: 'API Assessment' },
  { value: 'pentest', label: 'Penetration Test' },
  { value: 'red_team', label: 'Red Team Exercise' },
  { value: 'bug_bounty', label: 'Bug Bounty' },
];

const engagementStatuses = [
  { value: 'in_progress', label: 'In Progress', color: 'border-blue-500/30 text-blue-500 bg-blue-500/10' },
  { value: 'completed', label: 'Completed', color: 'border-green-500/30 text-green-500 bg-green-500/10' },
  { value: 'on_hold', label: 'On Hold', color: 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10' },
];

const emptyProduct = { name: '', description: '', product_type: 'web_application', lifecycle: 'production', business_criticality: 'medium', platform: '', origin: 'internal', tags: [] };
const emptyEngagement = { name: '', description: '', engagement_type: 'interactive', status: 'in_progress', target_start: '', target_end: '', lead: '', test_strategy: '', tags: [] };

export default function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ ...emptyProduct });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [trialLimitHit, setTrialLimitHit] = useState(false);

  // Product detail view
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productTab, setProductTab] = useState('engagements');
  const [engagements, setEngagements] = useState([]);
  const [productVulns, setProductVulns] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Engagement dialog
  const [showEngDialog, setShowEngDialog] = useState(false);
  const [engForm, setEngForm] = useState({ ...emptyEngagement });
  const [editEngagement, setEditEngagement] = useState(null);
  const [savingEng, setSavingEng] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editProduct) {
        const res = await axios.put(`${API}/products/${editProduct.product_id}`, form, { withCredentials: true });
        setProducts(prev => prev.map(p => p.product_id === editProduct.product_id ? res.data : p));
        toast.success('Product updated');
      } else {
        const res = await axios.post(`${API}/products`, form, { withCredentials: true });
        setProducts(prev => [res.data, ...prev]);
        toast.success('Product created');
      }
      setShowCreate(false);
      setEditProduct(null);
      setForm({ ...emptyProduct });
      setTrialLimitHit(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setTrialLimitHit(true);
        toast.error('Trial plan limited to 1 product. Upgrade to continue.');
      } else {
        toast.error('Failed to save product');
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product and all engagements?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      setProducts(prev => prev.filter(p => p.product_id !== productId));
      if (selectedProduct?.product_id === productId) setSelectedProduct(null);
      toast.success('Product deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (product) => {
    setForm({
      name: product.name, description: product.description, product_type: product.product_type,
      lifecycle: product.lifecycle, business_criticality: product.business_criticality,
      platform: product.platform || '', origin: product.origin || 'internal', tags: product.tags || [],
    });
    setEditProduct(product);
    setShowCreate(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(p => ({ ...p, tags: [...p.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => setForm(p => ({ ...p, tags: p.tags.filter(t => t !== tag) }));
  const getCritColor = (crit) => criticalities.find(c => c.value === crit)?.color || '';

  // Product detail
  const openProductDetail = async (product) => {
    setSelectedProduct(product);
    setProductTab('engagements');
    setLoadingDetail(true);
    try {
      const [engRes, vulnRes] = await Promise.all([
        axios.get(`${API}/products/${product.product_id}/engagements`, { withCredentials: true }),
        axios.get(`${API}/products/${product.product_id}/vulnerabilities`, { withCredentials: true }),
      ]);
      setEngagements(engRes.data);
      setProductVulns(vulnRes.data);
    } catch {} finally { setLoadingDetail(false); }
  };

  // Engagement CRUD
  const handleSaveEngagement = async () => {
    if (!engForm.name.trim() || !selectedProduct) return;
    setSavingEng(true);
    try {
      if (editEngagement) {
        const res = await axios.put(`${API}/engagements/${editEngagement.engagement_id}`, engForm, { withCredentials: true });
        setEngagements(prev => prev.map(e => e.engagement_id === editEngagement.engagement_id ? res.data : e));
        toast.success('Engagement updated');
      } else {
        const res = await axios.post(`${API}/engagements`, { ...engForm, product_id: selectedProduct.product_id }, { withCredentials: true });
        setEngagements(prev => [res.data, ...prev]);
        toast.success('Engagement created');
      }
      setShowEngDialog(false);
      setEditEngagement(null);
      setEngForm({ ...emptyEngagement });
    } catch { toast.error('Failed to save engagement'); }
    finally { setSavingEng(false); }
  };

  const deleteEngagement = async (engId) => {
    if (!window.confirm('Delete this engagement?')) return;
    try {
      await axios.delete(`${API}/engagements/${engId}`, { withCredentials: true });
      setEngagements(prev => prev.filter(e => e.engagement_id !== engId));
      toast.success('Engagement deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openEditEngagement = (eng) => {
    setEngForm({
      name: eng.name, description: eng.description, engagement_type: eng.engagement_type,
      status: eng.status, target_start: eng.target_start || '', target_end: eng.target_end || '',
      lead: eng.lead || '', test_strategy: eng.test_strategy || '', tags: eng.tags || [],
    });
    setEditEngagement(eng);
    setShowEngDialog(true);
  };

  const getStatusColor = (status) => engagementStatuses.find(s => s.value === status)?.color || '';

  // If viewing a product detail
  if (selectedProduct) {
    const sevCounts = {
      critical: productVulns.filter(v => v.severity === 'critical').length,
      high: productVulns.filter(v => v.severity === 'high').length,
      medium: productVulns.filter(v => v.severity === 'medium').length,
      low: productVulns.filter(v => v.severity === 'low').length,
    };
    return (
      <div className="space-y-6" data-testid="product-detail-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSelectedProduct(null)} data-testid="back-to-products">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="product-detail-title">{selectedProduct.name}</h1>
            <p className="text-sm text-muted-foreground mt-1 capitalize">{selectedProduct.product_type?.replace('_', ' ')} &middot; {selectedProduct.lifecycle}</p>
          </div>
          <Badge variant="outline" className={getCritColor(selectedProduct.business_criticality)}>{selectedProduct.business_criticality}</Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{engagements.length}</p><p className="text-xs text-muted-foreground">Engagements</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{productVulns.length}</p><p className="text-xs text-muted-foreground">Total Findings</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{sevCounts.critical}</p><p className="text-xs text-muted-foreground">Critical</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-orange-500">{sevCounts.high}</p><p className="text-xs text-muted-foreground">High</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-500">{sevCounts.medium}</p><p className="text-xs text-muted-foreground">Medium</p></CardContent></Card>
        </div>

        <Tabs value={productTab} onValueChange={setProductTab}>
          <TabsList data-testid="product-detail-tabs">
            <TabsTrigger value="engagements">Engagements ({engagements.length})</TabsTrigger>
            <TabsTrigger value="findings">Findings ({productVulns.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="engagements" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Testing engagements for this product</p>
              <Dialog open={showEngDialog} onOpenChange={(v) => { setShowEngDialog(v); if (!v) { setEditEngagement(null); setEngForm({ ...emptyEngagement }); } }}>
                <DialogTrigger asChild>
                  <Button className="gap-2" size="sm" data-testid="add-engagement-btn"><Plus className="h-4 w-4" /> New Engagement</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg" data-testid="engagement-dialog">
                  <DialogHeader><DialogTitle>{editEngagement ? 'Edit Engagement' : 'New Engagement'}</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-2 max-h-[65vh] overflow-y-auto">
                    <div><Label>Name *</Label><Input value={engForm.name} onChange={e => setEngForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Q1 2026 Pentest" data-testid="eng-name-input" /></div>
                    <div><Label>Description</Label><Textarea value={engForm.description} onChange={e => setEngForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Describe the engagement scope..." data-testid="eng-desc-input" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Type</Label>
                        <Select value={engForm.engagement_type} onValueChange={v => setEngForm(p => ({ ...p, engagement_type: v }))}>
                          <SelectTrigger data-testid="eng-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{engagementTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Status</Label>
                        <Select value={engForm.status} onValueChange={v => setEngForm(p => ({ ...p, status: v }))}>
                          <SelectTrigger data-testid="eng-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{engagementStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Start Date</Label><Input type="date" value={engForm.target_start?.split('T')[0] || ''} onChange={e => setEngForm(p => ({ ...p, target_start: e.target.value }))} data-testid="eng-start-input" /></div>
                      <div><Label>End Date</Label><Input type="date" value={engForm.target_end?.split('T')[0] || ''} onChange={e => setEngForm(p => ({ ...p, target_end: e.target.value }))} data-testid="eng-end-input" /></div>
                    </div>
                    <div><Label>Test Strategy</Label><Textarea value={engForm.test_strategy} onChange={e => setEngForm(p => ({ ...p, test_strategy: e.target.value }))} rows={2} placeholder="Testing approach..." data-testid="eng-strategy-input" /></div>
                    <Button onClick={handleSaveEngagement} className="w-full" disabled={savingEng || !engForm.name.trim()} data-testid="save-engagement-btn">
                      {savingEng ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {editEngagement ? 'Update' : 'Create'} Engagement
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loadingDetail ? (
              <div className="flex items-center justify-center h-[200px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : engagements.length === 0 ? (
              <Card><CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No engagements yet. Create your first engagement to start testing.</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {engagements.map(eng => (
                  <Card key={eng.engagement_id} className="group hover:border-primary/30 transition-colors" data-testid={`engagement-${eng.engagement_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="p-2 rounded-lg bg-primary/10 mt-0.5"><Target className="h-4 w-4 text-primary" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-medium text-sm">{eng.name}</h3>
                              <Badge variant="outline" className={`text-xs ${getStatusColor(eng.status)}`}>{eng.status?.replace('_', ' ')}</Badge>
                              <Badge variant="outline" className="text-xs capitalize">{eng.engagement_type?.replace('_', ' ')}</Badge>
                            </div>
                            {eng.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{eng.description}</p>}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Bug className="h-3 w-3" />{eng.finding_count || 0} findings</span>
                              <span className="flex items-center gap-1"><Shield className="h-3 w-3" />{eng.test_count || 0} tests</span>
                              {eng.target_start && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(eng.target_start).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => navigate(`/scanner?product_id=${selectedProduct.product_id}&engagement_id=${eng.engagement_id}`)} data-testid={`scan-eng-${eng.engagement_id}`}>
                            <Play className="h-3 w-3" /> Scan
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditEngagement(eng)} data-testid={`edit-eng-${eng.engagement_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteEngagement(eng.engagement_id)} data-testid={`delete-eng-${eng.engagement_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="findings" className="mt-4">
            {productVulns.length === 0 ? (
              <Card><CardContent className="flex flex-col items-center justify-center py-12">
                <Bug className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No findings yet. Run a scan to discover vulnerabilities.</p>
              </CardContent></Card>
            ) : (
              <Card><CardContent className="p-0">
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/30 sticky top-0"><tr>
                      <th className="text-left p-3 font-medium text-muted-foreground">Severity</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Title</th>
                      <th className="text-left p-3 font-medium text-muted-foreground hidden md:table-cell">Asset</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-3 font-medium text-muted-foreground hidden lg:table-cell">CVSS</th>
                    </tr></thead>
                    <tbody>
                      {productVulns.map(v => (
                        <tr key={v.vuln_id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/vulnerabilities?product_id=${selectedProduct.product_id}`)} data-testid={`product-vuln-${v.vuln_id}`}>
                          <td className="p-3"><Badge variant="outline" className={`text-xs ${critBadge(v.severity)}`}>{v.severity}</Badge></td>
                          <td className="p-3 max-w-[300px]"><p className="truncate font-medium">{v.title}</p></td>
                          <td className="p-3 text-xs text-muted-foreground font-mono hidden md:table-cell">{v.asset}</td>
                          <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{v.status?.replace('_', ' ')}</Badge></td>
                          <td className="p-3 font-mono text-xs hidden lg:table-cell">{v.cvss_score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Product listing view
  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="products-title">Products</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage products, engagements, and linked findings</p>
        </div>
        <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) { setEditProduct(null); setForm({ ...emptyProduct }); setTrialLimitHit(false); } }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add Product</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2 max-h-[65vh] overflow-y-auto">
              {trialLimitHit && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm">
                  <p className="font-medium text-destructive flex items-center gap-2"><CreditCard className="h-4 w-4" /> Trial Limit Reached</p>
                  <p className="text-xs text-muted-foreground mt-1">Free trial is limited to 1 product. Upgrade to Premium for unlimited products.</p>
                  <Button size="sm" className="mt-2 gap-1" onClick={() => { setShowCreate(false); navigate('/pricing'); }} data-testid="upgrade-btn">
                    <CreditCard className="h-3 w-3" /> Upgrade Now
                  </Button>
                </div>
              )}
              <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Customer Portal" data-testid="product-name-input" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." rows={2} data-testid="product-desc-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Product Type</Label>
                  <Select value={form.product_type} onValueChange={v => setForm(p => ({ ...p, product_type: v }))}>
                    <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{productTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Lifecycle</Label>
                  <Select value={form.lifecycle} onValueChange={v => setForm(p => ({ ...p, lifecycle: v }))}>
                    <SelectTrigger data-testid="product-lifecycle-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{lifecycles.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Business Criticality</Label>
                  <Select value={form.business_criticality} onValueChange={v => setForm(p => ({ ...p, business_criticality: v }))}>
                    <SelectTrigger data-testid="product-criticality-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{criticalities.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Platform</Label>
                  <Input value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} placeholder="e.g., React, Node.js" data-testid="product-platform-input" />
                </div>
              </div>
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..." className="flex-1" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} data-testid="product-tag-input" />
                  <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="gap-1 cursor-pointer" onClick={() => removeTag(tag)}>
                        {tag} <span className="text-muted-foreground">&times;</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleSave} className="w-full" disabled={saving || !form.name.trim()} data-testid="save-product-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : products.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-16">
          <Package className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">No products yet. Add your first product to start testing.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {products.map(product => (
            <Card key={product.product_id} className="metric-card group cursor-pointer hover:border-primary/40 transition-all" onClick={() => openProductDetail(product)} data-testid={`product-${product.product_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10"><Package className="h-4 w-4 text-primary" /></div>
                    <div>
                      <h3 className="font-medium text-sm">{product.name}</h3>
                      <p className="text-xs text-muted-foreground capitalize">{product.product_type?.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(product); }} data-testid={`edit-product-${product.product_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(product.product_id); }} data-testid={`delete-product-${product.product_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {product.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{product.description}</p>}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-xs ${getCritColor(product.business_criticality)}`}>{product.business_criticality}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{product.lifecycle}</Badge>
                  {product.platform && <Badge variant="outline" className="text-xs">{product.platform}</Badge>}
                </div>
                {product.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {product.tags.map(tag => <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">{tag}</Badge>)}
                  </div>
                )}
                <Separator className="my-3" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Target className="h-3 w-3" />{product.engagement_count || 0} engagements</span>
                  <span className="flex items-center gap-1"><Bug className="h-3 w-3" />{product.finding_count || 0} findings</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function critBadge(severity) {
  const map = {
    critical: 'border-red-500/30 text-red-500 bg-red-500/10',
    high: 'border-orange-500/30 text-orange-500 bg-orange-500/10',
    medium: 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10',
    low: 'border-green-500/30 text-green-500 bg-green-500/10',
    informational: 'border-blue-500/30 text-blue-500 bg-blue-500/10',
  };
  return map[severity] || '';
}
